import { useParams, useLocation, useSearch, Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { format, formatDistanceToNow, isFuture } from "date-fns";
import {
  Calendar, Clock, Users,
  AlertCircle, ArrowRight,
} from "lucide-react";
import { LiveBroadcastIcon } from "@/components/live-broadcast-icon";
import { useState, useEffect } from "react";
import { LANGUAGES, type Session } from "@shared/schema";
import { DEFAULT_THUMBNAIL } from "@/lib/constants";

interface RoomClass {
  id: string;
  title: string;
  description: string | null;
  language: string;
  category: string | null;
  thumbnailUrl: string | null;
  sessionCount: number;
  liveCount: number;
  upcomingCount: number;
}

interface RoomData {
  provider: {
    displayName: string;
    bio: string | null;
    avatarUrl: string | null;
    roomSlug: string;
  };
  liveSession: (Session & { classTitle: string; language: string; thumbnailUrl: string | null }) | null;
  upcomingSessions: (Session & { classTitle: string; language: string; thumbnailUrl: string | null })[];
  classes: RoomClass[];
}

function getFlagEmoji(code: string) {
  return code
    .toUpperCase()
    .replace(/./g, (c) => String.fromCodePoint(0x1f1e6 - 65 + c.charCodeAt(0)));
}

export default function JoinRoom() {
  const { slug } = useParams<{ slug: string }>();
  const { user, isLoading: authLoading } = useAuth();
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  const searchParams = useSearch();
  const sessionParam = new URLSearchParams(searchParams).get("session") || "";
  const [autoRedirected, setAutoRedirected] = useState(false);

  const roomQueryPath = sessionParam
    ? `/api/room/${slug}?session=${sessionParam}`
    : `/api/room/${slug}`;

  const { data: room, isLoading, isError } = useQuery<RoomData>({
    queryKey: ["/api/room", slug, sessionParam],
    queryFn: async () => {
      const res = await fetch(roomQueryPath, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load room");
      return res.json();
    },
    enabled: !!slug,
    retry: 1,
  });

  useEffect(() => {
    if (authLoading) return;
    if (!room?.liveSession || autoRedirected) return;
    setAutoRedirected(true);
    navigate(`/live/${room.liveSession.id}`);
  }, [room, user, authLoading, autoRedirected, navigate]);

  if (isLoading || authLoading) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-12">
        <div className="text-center">
          <Skeleton className="mx-auto mb-4 h-20 w-20 rounded-full" />
          <Skeleton className="mx-auto mb-2 h-8 w-48" />
          <Skeleton className="mx-auto h-4 w-72" />
        </div>
      </div>
    );
  }

  if (isError || !room) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-12">
        <div className="glass-panel depth-shadow-md p-8 text-center">
          <div className="empty-state-icon mb-4">
            <AlertCircle className="h-12 w-12 text-primary/25" />
          </div>
          <h1 className="text-xl font-bold glow-text-sm" data-testid="text-room-not-found">
            {t("room.notFound")}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground/50">
            {t("room.notFoundDesc")}
          </p>
          <Link href="/">
            <Button className="mt-6" data-testid="button-go-home">
              {t("room.goHome")}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const { provider, liveSession, upcomingSessions, classes: providerClasses } = room;

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 sm:py-12">
      <div className="glass-page-bg" />
      <div className="mb-8 text-center">
        <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full border border-primary/20" style={{ background: "linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(37, 99, 235, 0.05))", boxShadow: "0 0 30px rgba(59, 130, 246, 0.15), 0 0 0 1px rgba(59, 130, 246, 0.1) inset" }}>
          {provider.avatarUrl ? (
            <img
              src={provider.avatarUrl}
              alt={provider.displayName}
              className="h-full w-full rounded-full object-cover"
              data-testid="img-provider-avatar"
            />
          ) : (
            <span className="text-2xl font-bold text-primary" data-testid="text-provider-initial">
              {provider.displayName.charAt(0).toUpperCase()}
            </span>
          )}
        </div>
        <h1 className="text-2xl font-bold sm:text-3xl glow-text-sm" data-testid="text-provider-name">
          {provider.displayName}
        </h1>
        {provider.bio && (
          <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto" data-testid="text-provider-bio">
            {provider.bio}
          </p>
        )}
      </div>

      {liveSession && (
        <div className="glass-panel mb-6 overflow-hidden border-green-500/30 !bg-green-500/5" data-testid="card-live-session">
          <div className="p-5 sm:p-6">
            <div className="flex items-center gap-2 mb-3">
              <Badge variant="destructive" className="animate-pulse gap-1 flex items-center">
                <LiveBroadcastIcon size={14} color="white" />
                {t("room.liveNow")}
              </Badge>
            </div>
            <h2 className="text-lg font-semibold" data-testid="text-live-title">
              {liveSession.classTitle}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("room.startedAgo", { time: formatDistanceToNow(new Date(liveSession.startsAt)) })}
            </p>
            <Link href={`/live/${liveSession.id}`}>
              <Button size="lg" className="mt-4 w-full sm:w-auto btn-gradient-capsule btn-cta-glow" data-testid="button-join-live">
                <ArrowRight className="mr-2 h-4 w-4" />
                {t("room.joinLive")}
              </Button>
            </Link>
          </div>
        </div>
      )}

      {upcomingSessions.length > 0 && (
        <div className="mb-8">
          <h2 className="mb-4 text-lg font-semibold" data-testid="text-upcoming-heading">
            {t("room.upcomingSessions")}
          </h2>
          <div className="space-y-3">
            {upcomingSessions.map((session) => (
              <div key={session.id} className="glass-panel-sm p-4 sm:p-5" data-testid={`card-upcoming-${session.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <h3 className="font-medium truncate" data-testid={`text-session-title-${session.id}`}>
                      {session.classTitle}
                    </h3>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5" />
                        {format(new Date(session.startsAt), "MMM d, yyyy")}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" />
                        {format(new Date(session.startsAt), "h:mm a")}
                      </span>
                    </div>
                    {isFuture(new Date(session.startsAt)) && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        {t("room.startsIn", { time: formatDistanceToNow(new Date(session.startsAt)) })}
                      </p>
                    )}
                  </div>
                  <Link href={`/live/${session.id}`}>
                    <Button size="sm" variant="outline" data-testid={`button-view-session-${session.id}`}>
                      {t("room.viewSession")}
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {providerClasses && providerClasses.length > 0 && (
        <div className="mb-8">
          <h2 className="mb-4 text-lg font-semibold flex items-center gap-2" data-testid="text-classes-heading">
            <Users className="h-5 w-5 text-primary" />
            {provider.displayName}'s Classes ({providerClasses.length})
          </h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {providerClasses.map((cls) => {
              const lang = LANGUAGES.find((l) => l.value === cls.language);
              return (
                <Link key={cls.id} href={`/classes/${cls.id}`}>
                  <div className="glass-tile overflow-hidden cursor-pointer group" data-testid={`card-room-class-${cls.id}`}>
                    <div className="relative h-32 w-full overflow-hidden">
                      <img
                        src={cls.thumbnailUrl || DEFAULT_THUMBNAIL}
                        alt={cls.title}
                        className="h-full w-full object-cover transition-transform group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      <div className="absolute bottom-2 left-3 right-3 flex items-center gap-1.5">
                        {cls.liveCount > 0 && (
                          <Badge variant="destructive" className="gap-1 text-[10px] flex items-center">
                            <LiveBroadcastIcon size={11} color="white" /> Live
                          </Badge>
                        )}
                        {cls.upcomingCount > 0 && (
                          <Badge variant="outline" className="bg-amber-500/20 border-amber-500/40 text-amber-200 text-[10px]">
                            <Clock className="mr-0.5 h-2.5 w-2.5" /> {cls.upcomingCount} upcoming
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold truncate" data-testid={`text-room-class-title-${cls.id}`}>
                        {cls.title}
                      </h3>
                      {cls.description && (
                        <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{cls.description}</p>
                      )}
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        {lang && (
                          <Badge variant="outline" className="text-[10px]">
                            {getFlagEmoji(lang.flag)} {lang.label}
                          </Badge>
                        )}
                        {cls.category && (
                          <Badge variant="secondary" className="text-[10px]">{cls.category}</Badge>
                        )}
                        <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                          <Calendar className="h-2.5 w-2.5" />
                          {cls.sessionCount} sessions
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {!liveSession && upcomingSessions.length === 0 && (!providerClasses || providerClasses.length === 0) && (
        <div className="glass-panel depth-shadow-md p-8 text-center" data-testid="card-no-sessions">
          <div className="empty-state-icon mb-4">
            <Calendar className="h-12 w-12 text-primary/25" />
          </div>
          <h2 className="text-lg font-semibold glow-text-sm">{t("room.noLiveSessions")}</h2>
          <p className="mt-2 text-sm text-muted-foreground/50 max-w-xs mx-auto leading-relaxed">
            {t("room.checkBackLater", { name: provider.displayName })}
          </p>
          <div className="divider-glow w-24 mx-auto my-5" />
        </div>
      )}
    </div>
  );
}
